import React, { useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  onRemove: (id: string) => void;
}

const toastConfig = {
  success: {
    icon: CheckCircleIcon,
    bgColor: 'bg-success/10',
    borderColor: 'border-success/20',
    iconBg: 'bg-success/20',
    textColor: 'text-gray-50',
    iconColor: 'text-success'
  },
  error: {
    icon: XCircleIcon,
    bgColor: 'bg-error/10',
    borderColor: 'border-error/20',
    iconBg: 'bg-error/20',
    textColor: 'text-gray-50',
    iconColor: 'text-error'
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/20',
    iconBg: 'bg-primary/20',
    textColor: 'text-gray-50',
    iconColor: 'text-primary'
  },
  info: {
    icon: InformationCircleIcon,
    bgColor: 'bg-info/10',
    borderColor: 'border-info/20',
    iconBg: 'bg-info/20',
    textColor: 'text-gray-50',
    iconColor: 'text-info'
  }
};

export default function Toast({ id, type, message, onRemove }: ToastProps) {
  const config = toastConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, 4000);

    return () => clearTimeout(timer);
  }, [id, onRemove]);

  return (
    <div
      className={`flex items-center gap-3 p-4 mb-3 rounded-xl border backdrop-blur-md bg-bg-elevated ${config.borderColor} shadow-xl animate-in slide-in-from-left duration-300`}
    >
      <div className={`flex-shrink-0 w-8 h-8 ${config.iconBg} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${config.iconColor}`} />
      </div>
      <p className={`flex-1 text-sm font-medium ${config.textColor}`}>{message}</p>
      <button
        onClick={() => onRemove(id)}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-50 hover:bg-white/10 rounded-md transition-all cursor-pointer"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
